import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import weekOfYear from 'dayjs/plugin/weekOfYear'

dayjs.extend(isBetween)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(localizedFormat)
dayjs.extend(timezone)
dayjs.extend(utc)
dayjs.extend(weekOfYear)

const dates = {
  now() {
    return dayjs.tz()
  },
  getUnixDate(date) {
    return dayjs.tz(date).unix()
  },
  getUtcDate(date) {
    return dayjs.utc(date)
  },
  getLocalDate(date) {
    return dayjs.tz(date).local()
  },
  getLocalUtcDate(date) {
    return dayjs.tz(date).utc().local()
  },

  get(date) {
    return this.getUtcDate(date)
  },
  getDate(date) {
    return this.get(date)
      .date()
  },
  getMonth(date) {
    return this.get(date)
      .month()
  },

  getFormattedDate(date, format = 'YYYY-MM-DD') {
    const formatDateString = () => !date?.format && this.getLocalDate(date).format(format)
    return formatDateString() || date.format(format)
  },
  getFormattedLocalDateTime(date) {
    return this.getFormattedDate(date, 'ddd, MMM DD, YYYY hh:mm A')
  },

  getDataFreshnessDateTime(date) {
    const paddedDateTime = this.getLocalUtcDate(date).subtract(1, 'hour')
    return this.getFormattedLocalDateTime(paddedDateTime)
  },

  getRelativeDate(relativeDateString) {
    const relativeDateMap = {
      today: '0 days',
      yesterday: '-1 day',
      tomorrow: '1 day',
    }
    const actualRelativeDateString = relativeDateMap[relativeDateString] || relativeDateString
    const relativeDateParts = actualRelativeDateString.split(/\s/)
    const [number, unit] = relativeDateParts
    const relativeDate = this.getLocalDate().add(Number(number), unit)

    return this.getFormattedDate(relativeDate, 'YYYY-MM-DD')
  },

  getStartOfWeek(timeShift, date) {
    const monday = dates.getLocalDate(date)
      .startOf('week')
      .add(1, 'day')
    return (timeShift && monday.add(timeShift, 'week')) || monday
  },
  getWeek(timeShift, date) {
    const startOfWeek = this.getStartOfWeek(timeShift, date)
    return [...Array(7)].map((_, dayIndex) => startOfWeek.add(dayIndex, 'day'))
  },
  getWeekOfYear(date) {
    return dates.getLocalDate(date).week()
  },

  isDateGte(utcItemDate, utcComparatorDate, unit = 'day') {
    return utcItemDate.isSameOrAfter(utcComparatorDate, unit)
  },
  isDateLte(utcItemDate, utcComparatorDate, unit = 'day') {
    return utcItemDate.isSameOrBefore(utcComparatorDate, unit)
  },
  isDateEqual(utcItemDate, utcComparatorDate, unit = 'day') {
    return utcItemDate.isSame(utcComparatorDate, unit)
  },
  isDateInRange(utcItemDate, utcRangeStart, utcRangeEnd, unit = 'day') {
    return utcItemDate.isBetween(utcRangeStart, utcRangeEnd, unit, '[]')
  },

  areDatesGte(itemDate, comparatorDate, unit) {
    const utcItemDate = this.get(itemDate)
    const utcComparatorDate = this.get(comparatorDate)

    return this.isDateGte(utcItemDate, utcComparatorDate, unit)
  },
  areDatesLte(itemDate, comparatorDate, unit) {
    const utcItemDate = this.get(itemDate)
    const utcComparatorDate = this.get(comparatorDate)

    return this.isDateLte(utcItemDate, utcComparatorDate, unit)
  },
  areDatesEqual(itemDate, comparatorDate, unit) {
    const utcItemDate = this.get(itemDate)
    const utcComparatorDate = this.get(comparatorDate)

    return this.isDateEqual(utcItemDate, utcComparatorDate, unit)
  },
  areDatesInRange(itemDate, startDate, endDate, unit) {
    const utcRangeStart = this.get(startDate)
    const utcRangeEnd = this.get(endDate)

    const utcItemDate = this.get(itemDate)

    return this.isDateInRange(utcItemDate, utcRangeStart, utcRangeEnd, unit)
  },

  monthsFromNow(date) {
    const entryDate = this.get(date).startOf('month')
    const currentDate = this.get().startOf('month')

    return entryDate.diff(currentDate, 'month')
  },
  getStartOfCurrentMonth(date) {
    return dates.get(date)
      .startOf('month')
  },
  getStartOfLastMonth(date) {
    return dates
      .get(date)
      .subtract(1, 'month')
      .startOf('month')
  },
  getEndOfThisMonth(date) {
    return dates.get(date)
      .endOf('month')
  },
  getEndOfNextMonth(date) {
    return dates
      .get(date)
      .add(1, 'month')
      .endOf('month')
  },
  isPreviousMonthLocked() {
    return dates.getDate() > 7
  },
  getStartOfMonth(getCurrentMonth) {
    const isPreviousMonthLocked = getCurrentMonth || dates.isPreviousMonthLocked()

    if (isPreviousMonthLocked) return dates.getStartOfCurrentMonth()

    return dates.getStartOfLastMonth()
  },
  getEndOfMonth(getNextMonth) {
    const isPreviousMonthLocked = getNextMonth || dates.isPreviousMonthLocked()

    if (isPreviousMonthLocked) return dates.getEndOfNextMonth()

    return dates.getEndOfThisMonth()
  },

  mtd() {
    const today = dates.getDate()
    const getCurrentMonth = today > 1

    const startDate = dates.getStartOfMonth(getCurrentMonth)
    const endDate = dates.get()

    return [startDate, endDate]
  },
  getThisMonth() {
    const today = dates.get()
    const startDate = today.startOf('month')
    const endDate = today.endOf('month')
    return [startDate, endDate]
  },
  getLastMonth() {
    const today = dates.get()
    const startDate = today.subtract(1, 'month').startOf('month')
    const endDate = today.subtract(1, 'month').endOf('month')
    return [startDate, endDate]
  },

  wtd() {
    const startDate = dates.get().startOf('week')
    const endDate = dates.get()
    return [startDate, endDate]
  },
  getThisWeek() {
    const today = dates.get()
    const startDate = today.startOf('week')
    const endDate = today.endOf('week')
    return [startDate, endDate]
  },
  getLastWeek() {
    const today = dates.get()
    const startDate = today.subtract(1, 'week').startOf('week')
    const endDate = today.subtract(1, 'week').endOf('week')
    return [startDate, endDate]
  },

  ytd() {
    const today = dates.getDate()
    const isFirstOfYear = dates.getMonth() === 0 && today === 1

    return isFirstOfYear ? dates.getLastYear() : dates.getThisYear()
  },
  getLastYear() {
    const today = dates.get()
    const startDate = today.subtract(1, 'year').startOf('year')
    const endDate = today.subtract(1, 'year').endOf('year')
    return [startDate, endDate]
  },
  getThisYear() {
    const startDate = dates.get().startOf('year')
    const endDate = dates.get()
    return [startDate, endDate]
  },
  setTimezone(timezoneString) {
    dayjs.tz.setDefault(timezoneString)
  },
}

export default dates
